import { useState } from 'react';
import { createPortal } from 'react-dom';
import { T, useIsDesktop } from '../reportTokens';
import { exportTable, exportTableMultiOutlet, type ExportCell, type ExportFormat } from '../../../utils/reportExport';
import { ExportScopeModal, type ExportScope } from './ExportScopeModal';
import { useOutletAccess } from '../../../hooks/useOutletAccess';
import { useBranch } from '../../../context/BranchContext';
import '../EODSummaryReport.scss';

/** What a section's `buildExport` hands back — the table plus optional export hints. */
type ExportTableShape = {
  headers: string[];
  rows: ExportCell[][];
  totalRow?: boolean;
  numericColumns?: number[];
};

function ExportSvg({ size = 12, color = T.t3 }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function ExportIcon({ name, size = 18, color = T.t3 }: { name: string; size?: number; color?: string }) {
  return (
    <span
      className="material-symbols-rounded"
      style={{ fontSize: size, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {name}
    </span>
  );
}

/**
 * Desktop export dropdown — "Export" button with CSV / PDF options.
 * Pass `loading` to show a shimmer while an async export is in progress.
 */
export function ReportExportMenu({
  onCSV,
  onExcel,
  onPDF,
  loading = false,
  iconOnly = false,
  disabled = false,
}: {
  onCSV: () => void;
  onExcel: () => void;
  onPDF: () => void;
  loading?: boolean;
  /** Render as a bare square icon button (no "Export" label) — for compact header placement. */
  iconOnly?: boolean;
  /** Disable the control entirely — e.g. there is no data to export. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    if (loading || disabled) return;
    setOpen((v) => !v);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={handleClick}
        className={iconOnly ? 'eod-download-btn eod-download-btn--icon' : 'eod-download-btn'}
        aria-label="Export"
        disabled={loading || disabled}
      >
        {loading ? (
          <span className={iconOnly ? 'eod-export-shimmer-bar eod-export-shimmer-bar--sm' : 'eod-export-shimmer-bar'} />
        ) : iconOnly ? (
          <ExportSvg size={14} color="#000000" />
        ) : (
          <>
            <ExportSvg size={12} />
            Export
          </>
        )}
      </button>
      {open && !loading && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setOpen(false)} />
          <div className="eod-export-dropdown">
            <div
              className="eod-export-dropdown__item"
              onClick={() => { onCSV(); setOpen(false); }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Export as CSV
            </div>
            <div className="eod-export-dropdown__divider" />
            <div
              className="eod-export-dropdown__item"
              onClick={() => { onExcel(); setOpen(false); }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Export as Excel
            </div>
            <div className="eod-export-dropdown__divider" />
            <div
              className="eod-export-dropdown__item"
              onClick={() => { onPDF(); setOpen(false); }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Export as PDF
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Mobile export button — icon button that opens a bottom sheet with CSV / PDF options.
 * Pass `loading` to show a shimmer while an async export is in progress.
 */
export function ReportMobileExportBtn({
  onCSV,
  onExcel,
  onPDF,
  loading = false,
  headerStyle = false,
  disabled = false,
}: {
  onCSV: () => void;
  onExcel: () => void;
  onPDF: () => void;
  loading?: boolean;
  /** Emphasized square style (darker fill + shadow, black icon) — for compact header placement. */
  headerStyle?: boolean;
  /** Disable the control entirely — e.g. there is no data to export. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading || disabled) return;
    setOpen(true);
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={headerStyle ? 'eod-mobile-export-btn eod-mobile-export-btn--header' : 'eod-mobile-export-btn'}
        aria-label="Export"
        disabled={loading || disabled}
      >
        {loading ? (
          <span className="eod-export-shimmer-bar eod-export-shimmer-bar--sm" />
        ) : (
          <ExportSvg size={14} color={headerStyle ? '#000000' : undefined} />
        )}
      </button>
      {open && createPortal(
        <div className="eod-mobile-export-overlay" onClick={() => setOpen(false)}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.3)' }} />
          <div className="eod-mobile-export-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="eod-mobile-export-sheet__handle-row">
              <div className="eod-mobile-export-sheet__handle" />
            </div>
            <div className="eod-mobile-export-sheet__title">Export</div>
            <div
              className="eod-mobile-export-sheet__item"
              onClick={() => { onCSV(); setOpen(false); }}
            >
              <div className="eod-mobile-export-sheet__icon eod-mobile-export-sheet__icon--csv">
                <ExportIcon name="table_chart" size={18} color={T.green} />
              </div>
              <div>
                <div className="eod-mobile-export-sheet__item-title">Export as CSV</div>
                <div className="eod-mobile-export-sheet__item-desc">Spreadsheet-ready format</div>
              </div>
            </div>
            <div className="eod-mobile-export-sheet__divider" />
            <div
              className="eod-mobile-export-sheet__item"
              onClick={() => { onExcel(); setOpen(false); }}
            >
              <div className="eod-mobile-export-sheet__icon eod-mobile-export-sheet__icon--csv">
                <ExportIcon name="grid_on" size={18} color={T.green} />
              </div>
              <div>
                <div className="eod-mobile-export-sheet__item-title">Export as Excel</div>
                <div className="eod-mobile-export-sheet__item-desc">Real .xlsx workbook</div>
              </div>
            </div>
            <div className="eod-mobile-export-sheet__divider" />
            <div
              className="eod-mobile-export-sheet__item"
              onClick={() => { onPDF(); setOpen(false); }}
            >
              <div className="eod-mobile-export-sheet__icon eod-mobile-export-sheet__icon--pdf">
                <ExportIcon name="picture_as_pdf" size={18} color={T.accent} />
              </div>
              <div>
                <div className="eod-mobile-export-sheet__item-title">Export as PDF</div>
                <div className="eod-mobile-export-sheet__item-desc">Print-ready document</div>
              </div>
            </div>
            <div className="eod-mobile-export-sheet__cancel-row">
              <button onClick={() => setOpen(false)} className="eod-mobile-export-sheet__cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/** One entry in a {@link ReportExportOptionsMenu}. */
export interface ReportExportOption {
  /** Menu label, e.g. "Export as CSV". */
  label: string;
  /** Material symbol for the mobile sheet's tile. */
  icon?: string;
  iconColor?: string;
  /**
   * Tile modifier class (`eod-mobile-export-sheet__icon--csv` etc). The base
   * class carries no background, so without one the tile renders untinted.
   */
  iconClass?: string;
  /** Sub-label, mobile sheet only. */
  description?: string;
  onSelect: () => void;
}

/**
 * Export button with an arbitrary list of options — desktop dropdown, mobile
 * bottom sheet, same chrome as {@link ReportExportMenu}.
 *
 * That component hard-codes CSV/PDF; this one takes whatever the section offers
 * (Sales by Channel adds Excel). Kept as a separate component rather than
 * generalising `ReportExportMenu` in place, so the many existing callers of that
 * one keep their exact behaviour.
 */
export function ReportExportOptionsMenu({
  options,
  variant,
  loading = false,
  label = 'Export',
}: {
  options: ReportExportOption[];
  /** Force a layout; omit to auto-detect via `useIsDesktop()`. */
  variant?: 'mobile' | 'desktop';
  loading?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const resolved = variant ?? (isDesktop ? 'desktop' : 'mobile');

  const choose = (opt: ReportExportOption) => { opt.onSelect(); setOpen(false); };

  if (resolved === 'desktop') {
    return (
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => { if (!loading) setOpen((v) => !v); }}
          className="eod-download-btn"
          aria-label={label}
          disabled={loading}
        >
          {loading ? <span className="eod-export-shimmer-bar" /> : (<><ExportSvg size={12} />{label}</>)}
        </button>
        {open && !loading && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setOpen(false)} />
            <div className="eod-export-dropdown">
              {options.map((opt, i) => (
                <div key={opt.label}>
                  {i > 0 && <div className="eod-export-dropdown__divider" />}
                  <div
                    className="eod-export-dropdown__item"
                    onClick={() => choose(opt)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {opt.label}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={(e) => { e.stopPropagation(); if (!loading) setOpen(true); }}
        className="eod-mobile-export-btn"
        aria-label={label}
        disabled={loading}
      >
        {loading ? <span className="eod-export-shimmer-bar eod-export-shimmer-bar--sm" /> : <ExportSvg size={14} />}
      </button>
      {open && createPortal(
        <div className="eod-mobile-export-overlay" onClick={() => setOpen(false)}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.3)' }} />
          <div className="eod-mobile-export-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="eod-mobile-export-sheet__handle-row">
              <div className="eod-mobile-export-sheet__handle" />
            </div>
            <div className="eod-mobile-export-sheet__title">Export</div>
            {options.map((opt, i) => (
              <div key={opt.label}>
                {i > 0 && <div className="eod-mobile-export-sheet__divider" />}
                <div className="eod-mobile-export-sheet__item" onClick={() => choose(opt)}>
                  <div className={`eod-mobile-export-sheet__icon${opt.iconClass ? ` ${opt.iconClass}` : ''}`}>
                    <ExportIcon name={opt.icon ?? 'description'} size={18} color={opt.iconColor ?? T.t2} />
                  </div>
                  <div>
                    <div className="eod-mobile-export-sheet__item-title">{opt.label}</div>
                    {opt.description && <div className="eod-mobile-export-sheet__item-desc">{opt.description}</div>}
                  </div>
                </div>
              </div>
            ))}
            <div className="eod-mobile-export-sheet__cancel-row">
              <button onClick={() => setOpen(false)} className="eod-mobile-export-sheet__cancel-btn">Cancel</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Just the export TRIGGER — same button chrome and icon as the controls above,
 * but you supply the click behaviour.
 *
 * For sections whose export is not the standard CSV/PDF menu (e.g. Sales by
 * Channel, which opens its own outlet-scope dialog) so they still render as the
 * same control instead of a bespoke button. Lives here, next to the styles it
 * uses, so callers don't have to import `EODSummaryReport.scss` themselves —
 * pulling that global stylesheet into another module's import graph risks
 * reordering the CSS cascade for everything else that depends on it.
 */
export function ReportExportTriggerButton({
  onClick,
  variant,
  label = 'Export',
}: {
  onClick: () => void;
  /** Force a layout; omit to auto-detect via `useIsDesktop()`. */
  variant?: 'mobile' | 'desktop';
  /** Desktop button caption. Mobile is always icon-only. */
  label?: string;
}) {
  const isDesktop = useIsDesktop();
  const resolved = variant ?? (isDesktop ? 'desktop' : 'mobile');
  return resolved === 'desktop' ? (
    <button type="button" onClick={onClick} className="eod-download-btn" aria-label={label}>
      <ExportSvg size={12} />
      {label}
    </button>
  ) : (
    <button type="button" onClick={onClick} className="eod-mobile-export-btn" aria-label={label}>
      <ExportSvg size={14} />
    </button>
  );
}

/**
 * Self-contained section export control — the ONE component to drop next to a
 * table's section header. Auto-detects desktop vs mobile layout (override with
 * `variant`), runs `buildExport` on CSV/PDF select (sync or async, with a
 * shimmer while it resolves), and hands the result to `exportTable` — so the
 * caller never touches CSV/PDF generation or web/native branching.
 */
export function ReportSectionExportButton({
  title,
  filename,
  buildExport,
  variant,
  iconOnly,
  dateRangeLabel,
  outletScoped = true,
  disabled
}: {
  /** PDF heading + section name (UI label). */
  title: string;
  /** Base file name, NO extension. */
  filename: string;
  /**
   * Returns the table's headers + rows (whole dataset). May be async.
   * Receives the target `format` so callers can shape cells per format — e.g.
   * comma-grouped monetary values for PDF, but raw numbers for CSV (commas
   * break arithmetic in spreadsheets). Format-agnostic callers may ignore it.
   * `opts.locationId`, when present, is an outlet OTHER than the currently
   * selected one — an "All outlets" export from the Scope modal calls this
   * once per accessible outlet with that outlet's id; implementations that
   * fetch from an API must use `opts?.locationId ?? <their own locationId>`
   * to honor it, or "All outlets" will silently repeat the current outlet's
   * data instead of that outlet's.
   *
   * May also return `numericColumns` — 0-based indexes of quantity columns that
   * must stay numeric (right-aligned, summable) in CSV. See ExportTableArgs.
   * Single-outlet exports only: the "All outlets" merge prepends an Outlet
   * column, which would shift every index.
   */
  buildExport: (format: ExportFormat, opts?: { locationId?: string }) => Promise<ExportTableShape> | ExportTableShape;
  /** Force a layout; omit to auto-detect via `useIsDesktop()`. */
  variant?: 'mobile' | 'desktop';
  /** Emphasized header style — icon-only square on desktop, darker/shadowed square + black icon on mobile. */
  iconOnly?: boolean;
  /**
   * Human-readable active date range (e.g. "Aug 7, 2026 – Aug 13, 2026") — shown in the file
   * header for BOTH scopes since DPFIX-07. It used to reach the "All outlets" file only, so
   * the ordinary "This outlet" download recorded nothing about the period it covered, and the
   * two scopes of the same export disagreed about what the file was. A report that omits it
   * still ships a file that does not say which days it describes.
   */
  dateRangeLabel?: string;
  /** Set false to suppress the "All outlets" scope option for a KPI that isn't outlet-attributable (e.g. a program-wide metric). Excel/CSV/PDF format choice is unaffected. */
  outletScoped?: boolean;
  disabled?:any;
}) {
  const isDesktop = useIsDesktop();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string | undefined>(undefined);
  const { outlets, isMultiOutlet } = useOutletAccess();
  const { selectedBranch: branch } = useBranch();

  const run = async (format: ExportFormat) => {
    if (loading) return;
    setLoading(true);
    try {
      const { headers, rows, totalRow, numericColumns } = await buildExport(format);
      await exportTable({ title, headers, rows, filename, format, totalRow, numericColumns, dateRangeLabel });
    } catch {
      // swallow — export failure should not crash the screen
    } finally {
      setLoading(false);
    }
  };

  const currentOutletName = (branch as any)?.locationName ?? (branch as any)?.name ?? 'This outlet';
  const brandName = (currentOutletName.includes(',') ? currentOutletName.split(',')[0] : currentOutletName).trim();

  const runScoped = async (scope: ExportScope, format: ExportFormat) => {
    if (loading) return;
    setLoading(true);
    try {
      if (scope === 'outlet') {
        const { headers, rows, totalRow, numericColumns } = await buildExport(format);
        await exportTable({ title, headers, rows, filename, format, totalRow, numericColumns, dateRangeLabel });
      } else {
        await exportTableMultiOutlet({
          title,
          filename,
          format,
          outlets: outlets.map((o) => ({ id: o.id, name: o.name, country: o.country })),
          brandName,
          dateRangeLabel: dateRangeLabel ?? 'Selected period',
          fetchForOutlet: (outlet) => Promise.resolve(buildExport(format, { locationId: outlet.id })),
          onProgress: (done, total) => setProgressLabel(`Fetching outlet ${done} / ${total}…`),
        });
      }
      setModalOpen(false);
    } catch {
      // swallow — export failure should not crash the screen
    } finally {
      setLoading(false);
      setProgressLabel(undefined);
    }
  };

  const resolved = variant ?? (isDesktop ? 'desktop' : 'mobile');

  if (outletScoped && isMultiOutlet) {
    return (
      <>
        {resolved === 'desktop' ? (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className={iconOnly ? 'eod-download-btn eod-download-btn--icon' : 'eod-download-btn'}
            aria-label="Export"
            disabled={loading}
          >
            {loading ? (
              <span className={iconOnly ? 'eod-export-shimmer-bar eod-export-shimmer-bar--sm' : 'eod-export-shimmer-bar'} />
            ) : iconOnly ? (
              <ExportSvg size={14} color="#000000" />
            ) : (
              <>
                <ExportSvg size={12} />
                Export
              </>
            )}
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}
            className={iconOnly ? 'eod-mobile-export-btn eod-mobile-export-btn--header' : 'eod-mobile-export-btn'}
            aria-label="Export"
            disabled={loading}
          >
            {loading ? (
              <span className="eod-export-shimmer-bar eod-export-shimmer-bar--sm" />
            ) : (
              <ExportSvg size={14} color={iconOnly ? '#000000' : undefined} />
            )}
          </button>
        )}
        <ExportScopeModal
          title={title}
          open={modalOpen}
          onClose={() => { if (!loading) setModalOpen(false); }}
          currentOutletName={currentOutletName}
          outletCount={outlets.length}
          onConfirm={runScoped}
          loading={loading}
          progressLabel={progressLabel}
        />
      </>
    );
  }

  return resolved === 'desktop' ? (
    <ReportExportMenu loading={loading} onCSV={() => run('csv')} onExcel={() => run('excel')} onPDF={() => run('pdf')} iconOnly={iconOnly} disabled={disabled}/>
  ) : (
    <ReportMobileExportBtn loading={loading} onCSV={() => run('csv')} onExcel={() => run('excel')} onPDF={() => run('pdf')} headerStyle={iconOnly} disabled={disabled}/>
  );
}
